import { apiFetch } from "./client";

export type ServiceStatus =
  | "operational"
  | "degraded"
  | "partial_outage"
  | "major_outage"
  | "unknown";

export type IncidentSeverity = "degraded" | "partial" | "major";

export interface ServiceCard {
  key: string;
  displayName: string;
  kind: string;
  status: ServiceStatus;
  lastObservedAt: string | null;
  lastLatencyMs: number | null;
  group: string;
  subgroup: string | null;
  description: string | null;
  external: boolean;
}

export interface IncidentDTO {
  id: string;
  service: string;
  severity: IncidentSeverity;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  summary: string;
  cause: string | null;
}

export interface OverviewResponse {
  status: ServiceStatus;
  services: ServiceCard[];
  uptime60d: { percentage: number; daysComputed: number };
  activeIncidents: IncidentDTO[];
  recentlyResolvedIncidents: IncidentDTO[];
  generatedAt: string;
}

export interface DailyUptime {
  date: string;
  uptimePct: number;
  totalProbes: number;
  okProbes: number;
}

export interface UptimeServiceSummary {
  key: string;
  displayName: string;
  days: DailyUptime[];
  uptimePct: number;
  external: boolean;
}

export interface UptimeResponse {
  services: UptimeServiceSummary[];
  globalPct: number;
  daysComputed: number;
}

export type MetricsRange = "24h" | "7d" | "30d";

export interface MetricsBucket {
  ts: string;
  avgLatencyMs: number | null;
  p95LatencyMs: number | null;
  okRatio: number;
  totalProbes: number;
}

export interface MetricsResponse {
  range: MetricsRange;
  serviceKey: string | null;
  bucketMinutes: number;
  buckets: MetricsBucket[];
}

export function getOverview(): Promise<OverviewResponse> {
  return apiFetch<OverviewResponse>("/status/overview");
}

export function getIncidents(params: {
  status?: "active" | "resolved" | "all";
  service?: string;
  since?: string;
  until?: string;
  limit?: number;
} = {}): Promise<{ items: IncidentDTO[] }> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.service) q.set("service", params.service);
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiFetch<{ items: IncidentDTO[] }>(`/status/incidents${qs ? `?${qs}` : ""}`);
}

export function getUptime(days = 60): Promise<UptimeResponse> {
  return apiFetch<UptimeResponse>(`/status/uptime?days=${days}`);
}

export function getMetrics(
  range: MetricsRange,
  service?: string,
): Promise<MetricsResponse> {
  const q = new URLSearchParams({ range });
  if (service) q.set("service", service);
  return apiFetch<MetricsResponse>(`/status/metrics?${q}`);
}

export interface SubscribeResult {
  id: string;
  secret: string;
  verificationToken: string;
  status: "pending_verification";
}

export function subscribeWebhook(url: string, email?: string): Promise<SubscribeResult> {
  return apiFetch<SubscribeResult>("/status/webhooks/subscribe", {
    method: "POST",
    body: { url, ...(email ? { email } : {}) },
  });
}

export function verifyWebhook(token: string): Promise<{ id: string; status: "active" }> {
  return apiFetch<{ id: string; status: "active" }>("/status/webhooks/verify", {
    method: "POST",
    body: { token },
  });
}

// ── Domain health ──────────────────────────────────────────────────────────

export type DomainProbeStatus = "operational" | "degraded" | "down" | "unknown";

export interface SubdomainHealth {
  subdomain: string;
  url: string;
  status: DomainProbeStatus;
  latencyMs: number | null;
  checkedAt: string | null;
}

export interface DomainHealth {
  domain: string;
  status: DomainProbeStatus;
  subdomains: SubdomainHealth[];
}

export interface DomainHealthResponse {
  domains: DomainHealth[];
  generatedAt: string;
}

export function getDomainHealth(): Promise<DomainHealthResponse> {
  return apiFetch<DomainHealthResponse>("/status/domains");
}
